import { FC, memo, ReactElement, useState } from 'react';
import { Button, message, Typography } from 'antd';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { Trans, useTranslation } from 'react-i18next';

import { cancelSaleInvestment } from 'features/myInvestments/actions';
import { buySecondaryMarketItem, getSecondaryMarketItems } from 'features/secondaryMarket/actions';
import { getBalance } from 'features/overview/actions';

import { ConfirmationModal, ImageLoader, Popover } from 'componets/ui';
import { CheckBoxField, FormItem } from 'componets/form';
import { RiskNotice } from '../../Invest/RiskNotice';

import { useAppDispatch, useAppSelector, useTranslate } from 'hooks';

import { ISecondaryMarketItem } from 'models';

import Utils from 'utils/utils';
import { Rules } from 'utils/constants';
import { isLt, isRo } from 'utils';

import { ReactComponent as Sell } from 'assets/icons/sell.svg';
import { ReactComponent as CancelSell } from 'assets/icons/cancel-sell.svg';
import { ReactComponent as NotSell } from 'assets/icons/not-sell.svg';
import { ReactComponent as DisabledIcon } from 'assets/icons/buy-investment.svg';

import s from './index.module.scss';
import i18next from 'i18next';

const DELAY = 3000;

type InvestmentSecondaryMarketProps = {
	activeSaleId?: string;
	isPending: boolean;
	isSell: boolean;
	isCancelSell: boolean;
	id?: number;
	projectId: number;
	isBuy?: boolean;
	isSecondaryMarket?: boolean;
	row?: ISecondaryMarketItem;
};

const WORDS = [
	'sellInvestmentText',
	'cancelSaleText',
	'tooltipSaleInProcessText',
	'cancelRequestQuestionText',
	'buyInvestmentText',
	'messageSuccessBuyText',
	'messageSaleCancelText',
	'detailsOfDealText',
	'projectText',
	'projectTypeText',
	'investmentAmountText',
	'interestRateText',
	'sellingPriceText',
	'yesText',
	'noText',
	'cancelText',
	'loanStatusText',
	'noteText',
];
export const InvestmentSecondaryMarket: FC<InvestmentSecondaryMarketProps> = memo(
	({
		activeSaleId,
		id,
		projectId,
		isPending,
		row,
		isSell,
		isCancelSell,
		isBuy,
		isSecondaryMarket,
	}) => {
		const params = useAppSelector((state) => state.secondaryMarket.params);
		const isAdmin = useAppSelector((state) => state.cookies.isAdmin);

		const [isSubmitting, setIsSubmitting] = useState(false);

		const {
			sellInvestmentText,
			cancelSaleText,
			tooltipSaleInProcessText,
			cancelRequestQuestionText,
			buyInvestmentText,
			messageSuccessBuyText,
			messageSaleCancelText,
			detailsOfDealText,
			projectText,
			projectTypeText,
			investmentAmountText,
			interestRateText,
			sellingPriceText,
			yesText,
			noText,
			cancelText,
			loanStatusText,
			noteText,
		} = useTranslate(WORDS);

		const dispatch = useAppDispatch();

		const { t } = useTranslation();

		const [isOpen, setIsOpen] = useState(false);
		const [isCanceled, setIsCanceled] = useState(false);
		const [isOpenAgreeRisk, setIsOpenAgreeRisk] = useState(false);

		const onCancel = () => {
			if (activeSaleId) {
				setIsSubmitting(true);
				dispatch(cancelSaleInvestment({ id: activeSaleId, isSecondaryMarket }))
					.unwrap()
					.then(() => {
						if (isSecondaryMarket) {
							setTimeout(() => {
								dispatch(getSecondaryMarketItems(params));
							}, DELAY);
						} else {
							setIsCanceled(true);
						}

						setIsOpenAgreeRisk(false);
						message.success(messageSaleCancelText);
					})
					.finally(() => {
						setIsSubmitting(false);
						setIsOpen(false);
					});
			}
		};

		const onBuy = () => {
			if (isBuy && activeSaleId) {
				setIsSubmitting(true);
				dispatch(buySecondaryMarketItem({ id: activeSaleId, isSecondaryMarket }))
					.unwrap()
					.then(() => {
						message.success(messageSuccessBuyText);
						setTimeout(() => {
							dispatch(getBalance());
							dispatch(getSecondaryMarketItems(params));
						}, DELAY);
					})
					.catch((e) => {
						message.error(t(e.message));
					})
					.finally(() => {
						setIsSubmitting(false);
						setIsOpen(false);
						setIsOpenAgreeRisk(false);
					});
			}
		};

		const getValue = (notes: any) => {
			const found = notes.find((el: any) => el.language === i18next.language)
			return found?.description;
		}

		const note = row && getValue(row.note);

		if (isBuy && !isCancelSell) {
			return (
				<div>
					<InvestButton content={buyInvestmentText} isButton>
						<Button
							className={cn(s.circle, { [s.grey]: isAdmin })}
							icon={<DisabledIcon className={s.green} />}
							onClick={() => setIsOpen(true)}
						/>
					</InvestButton>

					<ConfirmationModal
						loading={isSubmitting}
						submitText={buyInvestmentText}
						cancelText={cancelText}
						isOpen={isOpen}
						onClose={() => setIsOpen(false)}
						onAgree={() => setIsOpenAgreeRisk(true)}
						title={detailsOfDealText}
						text={
							<FormItem
								name="agree"
								rules={[Rules.REQUIRED_CHECKBOX]}
								className="checkbox modal-checkbox"
							>
								<CheckBoxField
									text={
										<Trans
											i18nKey="confirmAssigmentAgreementText"
											components={{
												agreement_link: (
													<a
														className="modal-agreeLink"
														href={
															isLt && 'https://crowdpear.com/wp-content/uploads/2023/08/Reikalavimo_teises_perleidimo_sutarties_bendrosios_salygos.pdf' ||
															isRo && 'https://crowdpear.com/wp-content/uploads/2023/08/General_terms_and_conditions_of_the_assignment_agreement.pdf' ||
															'https://crowdpear.com/wp-content/uploads/2023/08/General_terms_and_conditions_of_the_assignment_agreement.pdf'
														}
														type="button"
														target="_blank"
														rel="noreferrer"
													/>
												),
											}}
										/>
									}
								/>
							</FormItem>
						}
						info={
							row && (
								<div className={s.block}>
									<ImageLoader wrapperClassName={s.photo} photo={row.mainPhoto || ''} />

									<div>
										<Typography.Text className={s.bold}>{projectText}:&nbsp;</Typography.Text>
										<Link
											to={`/account/projects/${row.loanId}`}
											className={s.projectLink}
											target="_blank"
										>
											{row.projectTitle}
										</Link>
									</div>
									<div>
										<Typography.Text className={s.bold}>{projectTypeText}:&nbsp;</Typography.Text>
										<Typography.Text>{Utils.formatProjectType(row.loanTermType)}</Typography.Text>
									</div>

									<div>
										<Typography.Text className={s.bold}>
											{investmentAmountText}:&nbsp;
										</Typography.Text>
										<Typography.Text>{Utils.moneyFormat(+row.remainingPrincipal)}</Typography.Text>
									</div>

									<div>
										<Typography.Text className={s.bold}>{interestRateText}:&nbsp;</Typography.Text>
										<Typography.Text>{row.investmentInterest}%</Typography.Text>
									</div>

									<div>
										<Typography.Text className={s.bold}>{loanStatusText}:&nbsp;</Typography.Text>
										<Typography.Text type={row.loanStatus?.toLowerCase() === 'in recovery' ? 'danger' : undefined}>
											{t(row.loanStatus) || t('notSetText')}
										</Typography.Text>
									</div>

									{(row?.note?.length > 0 && note) && (
										<div>
											<Typography.Text type="danger" className={s.bold}>{noteText}:&nbsp;</Typography.Text>
											<Typography.Text type="danger">{note}</Typography.Text>
										</div>
									)}

									<div className={s.selling}>
										<Typography.Text className={s.bold}>{sellingPriceText}:&nbsp;</Typography.Text>
										<Typography.Text>{Utils.moneyFormat(+row.saleAmount)}</Typography.Text>
									</div>
								</div>
							)
						}
					/>

					<RiskNotice
						isOpen={isOpenAgreeRisk}
						onClose={() => setIsOpenAgreeRisk(false)}
						onFinish={onBuy}
						loading={isSubmitting}
					/>
				</div>
			);
		}

		if (isPending) {
			return (
				<InvestButton content={tooltipSaleInProcessText}>
					<div className={cn(s.circle, s.circleHidden)}>
						<DisabledIcon />
					</div>
				</InvestButton>
			);
		}

		if (isSell || isCanceled) {
			return (
				<InvestButton content={sellInvestmentText} isButton>
					<Link
						className={cn(s.circle, { [s.grey]: isAdmin })}
						to={`sale/${id}`}
						state={{ projectId }}
					>
						<Sell />
					</Link>
				</InvestButton>
			);
		}

		if (isCancelSell) {
			return (
				<>
					<InvestButton content={cancelSaleText} isButton>
						<Button
							className={cn(s.circle, { [s.grey]: isAdmin })}
							icon={<CancelSell />}
							onClick={() => setIsOpen(true)}
						/>
					</InvestButton>

					<ConfirmationModal
						loading={isSubmitting}
						isOpen={isOpen}
						onClose={() => setIsOpen(false)}
						onAgree={onCancel}
						title={cancelSaleText}
						text={cancelRequestQuestionText}
						submitText={yesText}
						cancelText={noText}
					/>
				</>
			);
		}

		return (
			<InvestButton
				content={
					<Trans
						i18nKey="investmentPurchasedText"
						components={{
							list_item: <li />,
							list: <ul className={s.list} />,
						}}
					/>
				}
			>
				<div className={cn(s.circle, s.circleHidden)}>
					<NotSell />
				</div>
			</InvestButton>
		);
	},
);

interface InvestButtonProps {
	children: ReactElement;
	content: string | ReactElement;
	isButton?: boolean;
}
const InvestButton: FC<InvestButtonProps> = ({ content, children, isButton }) => {
	return (
		<div className={s.popover}>
			<Popover
				zIndex={98}
				content={content}
				trigger="hover"
				placement="topLeft"
				isButton={isButton}
			>
				{children}
			</Popover>
		</div>
	);
};
